import { useEffect, useRef } from "react";
import {
  AI_DATA_MUTATED_EVENT,
  resolveSyncDomains,
  type AiDataMutatedDetail,
  type SyncDomain,
} from "./ai-sync";

/**
 * Subscribe a custom store / page to AI ("Velo") server-side mutations.
 *
 * Fires `onSync` (debounced) when a Velo tool that affects one of `domains`
 * completes for the current event. Used by data sources that can't be cheaply
 * invalidated through react-query (the seating + slotting zustand stores and the
 * guest-list provider); plain react-query pages are handled centrally by
 * {@link AiDataSyncBridge} instead and need no hook.
 *
 * `onSync` is kept in a ref so callers don't have to memoise it — the window
 * listener stays stable across renders.
 */
export function useAiDataSync(
  domains: SyncDomain | SyncDomain[],
  eventId: string | null | undefined,
  onSync: (detail: AiDataMutatedDetail) => void,
  opts: { debounceMs?: number } = {},
): void {
  const { debounceMs = 350 } = opts;
  const onSyncRef = useRef(onSync);
  useEffect(() => {
    onSyncRef.current = onSync;
  }, [onSync]);

  // Stable set of the domains this consumer cares about.
  const wanted = Array.isArray(domains) ? domains : [domains];
  const wantedKey = wanted.join(",");

  useEffect(() => {
    if (!eventId) return;
    const wantedSet = new Set(wanted);
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastDetail: AiDataMutatedDetail | null = null;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AiDataMutatedDetail>).detail;
      if (!detail || !detail.toolName) return;
      // All AI tools are event-scoped, so eventId is set; ignore other events.
      if (detail.eventId && detail.eventId !== eventId) return;
      const affected = resolveSyncDomains(detail.toolName);
      if (!affected.some((d) => wantedSet.has(d))) return;

      lastDetail = detail;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        if (lastDetail) onSyncRef.current(lastDetail);
      }, debounceMs);
    };

    window.addEventListener(AI_DATA_MUTATED_EVENT, handler);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(AI_DATA_MUTATED_EVENT, handler);
    };
    // wantedKey captures the domain list; eventId + debounceMs re-bind on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, wantedKey, debounceMs]);
}
